package com.schoolconnect.app.repository.student

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.request.StudentSubjectEnrollmentCreateRequest
import com.schoolconnect.app.datasource.network.request.StudentUpdateRequest
import com.schoolconnect.app.datasource.network.request.StudentWithSubjectEnrollmentsCreateRequest
import com.schoolconnect.app.datasource.network.request.StudentFilterRequest
import com.schoolconnect.app.datasource.network.response.StudentResponse
import com.schoolconnect.app.datasource.network.response.ClassSectionResponse
import com.schoolconnect.app.datasource.network.response.StudentListResponse
import com.schoolconnect.app.datasource.network.response.StudentSubjectListResponse
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.datasource.network.response.withServerTotalCount
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.ServerSort
import com.schoolconnect.app.feature.common.ServerSortOrder
import com.schoolconnect.app.repository.classsection.ClassSectionRepository
import com.schoolconnect.app.repository.classsubjectlink.ClassSubjectLinkRepository
import com.schoolconnect.app.repository.subject.SubjectRepository
import com.schoolconnect.app.repository.common.CachedValue
import com.schoolconnect.app.repository.common.loadPagedItems
import com.schoolconnect.app.repository.common.returnCachedAndRefresh
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlin.time.Clock

object StudentRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] StudentRepository"
    private const val COMPLETE_CLASS_ROSTER_PAGE_SIZE = 100
    // Quick Mark must not turn a mistaken/unfiltered backend count into a
    // multi-thousand-row browser workload. A class roster over 500 rows fails
    // explicitly rather than loading an incomplete roster and saving it.
    private const val COMPLETE_CLASS_ROSTER_MAX_PAGES = 5
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    private data class StudentsPageCacheKey(
        val schoolId: String,
        val query: String,
        val classSectionId: String?,
        val isActive: Boolean?,
        val pageIndex: Int,
        val pageSize: Int,
        val sort: String
    )

    private data class CachedStudentsPage(
        val key: StudentsPageCacheKey,
        val rows: List<StudentListResponse>
    )

    private data class CachedClassSectionLabels(
        val schoolId: String,
        val labels: CachedValue<Map<String, String>>
    )

    private var cachedStudentsPage: CachedStudentsPage? = null
    private var cachedClassSectionLabels: CachedClassSectionLabels? = null

    suspend fun loadSchoolStudentsPage(
        schoolId: String,
        query: String = "",
        classSectionId: String? = null,
        isActive: Boolean? = null,
        advancedFilter: StudentFilterRequest? = null,
        pageIndex: Int = 0,
        pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
        sort: ServerSort = ServerSort("admission_number", com.schoolconnect.app.feature.common.ServerSortOrder.ASC),
        forceRemote: Boolean = false
    ): Result<List<StudentListResponse>> {
        val normalizedSchoolId = schoolId.trim()
        if (normalizedSchoolId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId is required"))
        }

        val normalizedQuery = query.trim()
        val normalizedClassSectionId = classSectionId?.trim()?.takeIf { it.isNotBlank() }
        val safePageIndex = pageIndex.coerceAtLeast(0)
        val safePageSize = pageSize.coerceAtLeast(1)
        val cacheKey = StudentsPageCacheKey(
            schoolId = normalizedSchoolId,
            query = normalizedQuery,
            classSectionId = normalizedClassSectionId,
            isActive = isActive,
            pageIndex = safePageIndex,
            pageSize = safePageSize,
            sort = sort.cacheKey
        )

        cachedStudentsPage?.takeIf { !forceRemote && advancedFilter == null && it.key == cacheKey }?.let { cache ->
            scLog(
                "$TAG loadSchoolStudentsPage cacheHit school=$normalizedSchoolId q=${normalizedQuery.ifBlank { "<blank>" }} classSectionId=${normalizedClassSectionId ?: "<any>"} isActive=${isActive?.toString() ?: "<any>"} pageIndex=$safePageIndex pageSize=$safePageSize count=${cache.rows.size}"
            )
            return returnCachedAndRefresh(scope, TAG, "loadSchoolStudentsPage", "school=$normalizedSchoolId pageIndex=$safePageIndex pageSize=$safePageSize", cache.rows) {
                loadSchoolStudentsPage(normalizedSchoolId, query, normalizedClassSectionId, isActive, null, safePageIndex, safePageSize, sort, true)
            }
        }

        val offset = safePageIndex * safePageSize
        scLog(
            "$TAG loadSchoolStudentsPage start school=$normalizedSchoolId q=${normalizedQuery.ifBlank { "<blank>" }} classSectionId=${normalizedClassSectionId ?: "<any>"} isActive=${isActive?.toString() ?: "<any>"} pageIndex=$safePageIndex pageSize=$safePageSize offset=$offset forceRemote=$forceRemote"
        )
        return runCatching {
            val response = if (advancedFilter != null) {
                KtorApiClient.filterStudentsWithClassSections(
                    schoolId = normalizedSchoolId,
                    request = advancedFilter,
                    sortBy = sort.normalizedField,
                    order = sort.order.apiValue,
                    limit = safePageSize,
                    offset = offset
                ).getOrElse { throwable ->
                    scLog("$TAG loadSchoolStudentsPage advancedFailure school=$normalizedSchoolId", throwable)
                    throw throwable
                }
            } else {
                KtorApiClient.getStudentsWithClassSections(
                    schoolId = normalizedSchoolId,
                    classSectionId = normalizedClassSectionId,
                    query = normalizedQuery.ifBlank { null },
                    isActive = isActive,
                    limit = safePageSize,
                    offset = offset,
                    sortBy = sort.normalizedField,
                    order = sort.order.apiValue
                ).getOrElse { throwable ->
                    scLog(
                        "$TAG loadSchoolStudentsPage fetchFailure school=$normalizedSchoolId pageIndex=$safePageIndex pageSize=$safePageSize",
                        throwable
                    )
                    throw throwable
                }
            }
            val rows = response.distinctBy { it.id }
                .withServerTotalCount(response.serverTotalCountOrNull())
            cachedStudentsPage = CachedStudentsPage(cacheKey, rows)
            scLog(
                "$TAG loadSchoolStudentsPage complete school=$normalizedSchoolId q=${normalizedQuery.ifBlank { "<blank>" }} pageIndex=$safePageIndex pageSize=$safePageSize count=${rows.size}"
            )
            rows
        }.onFailure { throwable ->
            scLog(
                "$TAG loadSchoolStudentsPage failure school=$normalizedSchoolId q=${normalizedQuery.ifBlank { "<blank>" }} pageIndex=$safePageIndex pageSize=$safePageSize",
                throwable
            )
        }
    }

    /** Loads every active student in one school class section for bulk workflows. */
    suspend fun loadCompleteSchoolClassRoster(
        schoolId: String,
        classSectionId: String
    ): Result<List<StudentListResponse>> {
        val normalizedSchoolId = schoolId.trim()
        val normalizedClassSectionId = classSectionId.trim()
        if (normalizedSchoolId.isBlank()) return Result.failure(IllegalArgumentException("schoolId is required"))
        if (normalizedClassSectionId.isBlank()) return Result.failure(IllegalArgumentException("classSectionId is required"))

        return try {
            var expectedCount: Int? = null
            val pageSize = COMPLETE_CLASS_ROSTER_PAGE_SIZE
            val rows = loadPagedItems(
                tag = TAG,
                operation = "loadCompleteSchoolClassRoster",
                pageSize = pageSize,
                maxPages = COMPLETE_CLASS_ROSTER_MAX_PAGES,
                startContext = "school=$normalizedSchoolId classSectionId=$normalizedClassSectionId",
                totalCount = { checkNotNull(expectedCount) { "Student count is missing. Please retry." } }
            ) { offset ->
                currentCoroutineContext().ensureActive()
                val page = loadSchoolStudentsPage(
                    schoolId = normalizedSchoolId,
                    classSectionId = normalizedClassSectionId,
                    isActive = true,
                    pageIndex = offset / pageSize,
                    pageSize = pageSize,
                    sort = ServerSort("name", ServerSortOrder.ASC),
                    forceRemote = true
                ).getOrThrow()
                currentCoroutineContext().ensureActive()
                val pageCount = checkNotNull(page.serverTotalCountOrNull()) {
                    "Student count is missing. Please retry before marking attendance."
                }
                check(expectedCount == null || expectedCount == pageCount) {
                    "Student roster changed while loading. Please retry."
                }
                expectedCount = pageCount
                page
            }
            scLog("$TAG loadCompleteSchoolClassRoster success school=$normalizedSchoolId classSectionId=$normalizedClassSectionId rows=${rows.size}")
            Result.success(rows)
        } catch (error: CancellationException) {
            throw error
        } catch (error: Exception) {
            scLog("$TAG loadCompleteSchoolClassRoster failure school=$normalizedSchoolId classSectionId=$normalizedClassSectionId", error)
            Result.failure(error)
        }
    }

    suspend fun loadSchoolClassSectionLabels(
        schoolId: String,
        forceRemote: Boolean = false
    ): Result<Map<String, String>> {
        val normalizedSchoolId = schoolId.trim()
        if (normalizedSchoolId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId is required"))
        }

        cachedClassSectionLabels?.takeIf { !forceRemote && it.schoolId == normalizedSchoolId }?.let { cache ->
            scLog(
                "$TAG loadSchoolClassSectionLabels cacheHit school=$normalizedSchoolId count=${cache.labels.value.size}"
            )
            return Result.success(cache.labels.value)
        }

        scLog("$TAG loadSchoolClassSectionLabels start school=$normalizedSchoolId forceRemote=$forceRemote")
        return runCatching {
            val academicYearId = ClassSectionRepository.loadCurrentSchoolAcademicYearId(
                schoolId = normalizedSchoolId,
                forceRemote = forceRemote
            ).getOrElse { throw it }

            val labels = linkedMapOf<String, String>()
            var pageIndex = 0
            while (pageIndex < 100) {
                val page = ClassSectionRepository.loadSchoolClassSectionsPage(
                    schoolId = normalizedSchoolId,
                    academicYearId = academicYearId,
                    pageIndex = pageIndex,
                    pageSize = 100,
                    forceRemote = forceRemote
                ).getOrElse { throw it }

                page.items.forEach { classSection ->
                    labels[classSection.id.orEmpty()] = buildClassSectionLabel(classSection)
                }
                if (page.items.size < 100) break
                pageIndex += 1
            }

            cachedClassSectionLabels = CachedClassSectionLabels(
                schoolId = normalizedSchoolId,
                labels = CachedValue(labels, Clock.System.now().toEpochMilliseconds())
            )
            scLog(
                "$TAG loadSchoolClassSectionLabels complete school=$normalizedSchoolId count=${labels.size} academicYear=$academicYearId"
            )
            labels
        }.onFailure { throwable ->
            scLog("$TAG loadSchoolClassSectionLabels failure school=$normalizedSchoolId", throwable)
        }
    }

    suspend fun createStudent(
        request: StudentWithSubjectEnrollmentsCreateRequest
    ): Result<StudentResponse> = KtorApiClient.createStudentWithSubjects(request).onSuccess {
        invalidateCaches()
    }

    suspend fun updateStudent(
        studentId: String,
        request: StudentUpdateRequest
    ): Result<StudentResponse> = KtorApiClient.updateStudent(studentId, request).onSuccess {
        invalidateCaches()
    }

    suspend fun deleteStudent(studentId: String): Result<Unit> {
        val normalizedStudentId = studentId.trim()
        require(normalizedStudentId.isNotBlank()) { "studentId is required" }
        scLog("$TAG deleteStudent start studentId=$normalizedStudentId")
        return KtorApiClient.deleteStudent(normalizedStudentId)
            .onSuccess {
                invalidateCaches()
                scLog("$TAG deleteStudent complete studentId=$normalizedStudentId")
            }
            .onFailure { throwable ->
                scLog("$TAG deleteStudent failure studentId=$normalizedStudentId", throwable)
            }
    }

    suspend fun toggleStudentStatus(studentId: String, isActive: Boolean): Result<Unit> {
        val normalizedStudentId = studentId.trim()
        require(normalizedStudentId.isNotBlank()) { "studentId is required" }
        scLog("$TAG toggleStudentStatus start studentId=$normalizedStudentId isActive=$isActive")
        return KtorApiClient.toggleStudentStatus(normalizedStudentId, isActive)
            .onSuccess {
                invalidateCaches()
                scLog("$TAG toggleStudentStatus complete studentId=$normalizedStudentId isActive=$isActive")
            }
            .onFailure { throwable ->
                scLog("$TAG toggleStudentStatus failure studentId=$normalizedStudentId", throwable)
            }
    }

    suspend fun loadStudentSubjectCodes(
        schoolId: String,
        studentId: String,
        classSectionId: String,
        forceRemote: Boolean = false
    ): Result<List<String>> = runCatching {
        val enrollments = KtorApiClient.getStudentSubjectEnrollments(studentId).getOrElse { throw it }
        val links = ClassSubjectLinkRepository.loadClassSectionSubjectLinks(
            schoolId = schoolId,
            classSectionId = classSectionId,
            forceRemote = forceRemote
        ).getOrElse { throw it }
        val codeMap = SubjectRepository.loadSchoolSubjectShortCodeMap(schoolId, forceRemote).getOrElse { throw it }
        val linkById = links.associateBy { it.id.orEmpty() }
        enrollments.mapNotNull { enrollment ->
            val subjectId = linkById[enrollment.classSubjectLinkId.orEmpty()]?.subjectId.orEmpty()
            codeMap[subjectId]
        }.distinct()
    }

    suspend fun syncStudentSubjects(
        schoolId: String,
        studentId: String,
        classSectionId: String,
        selectedCodes: List<String>
    ): Result<Unit> = runCatching {
        val links = ClassSubjectLinkRepository.loadClassSectionSubjectLinks(schoolId, classSectionId, forceRemote = true)
            .getOrElse { throw it }
        val codeMap = SubjectRepository.loadSchoolSubjectShortCodeMap(schoolId, forceRemote = true).getOrElse { throw it }
        val selectedLinkIds = links.filter { link ->
            selectedCodes.any { it.equals(codeMap[link.subjectId.orEmpty()], ignoreCase = true) }
        }.mapNotNull { it.id }.toSet()
        val existing = KtorApiClient.getStudentSubjectEnrollments(studentId).getOrElse { throw it }
        val existingByLink = existing.filter { it.isActive != false }.associateBy { it.classSubjectLinkId.orEmpty() }
        selectedLinkIds.filterNot { existingByLink.containsKey(it) }.forEach { linkId ->
            KtorApiClient.createStudentSubjectEnrollment(
                StudentSubjectEnrollmentCreateRequest(studentId = studentId, classSubjectLinkId = linkId)
            ).getOrElse { throw it }
        }
        existing.filter { it.isActive != false && it.classSubjectLinkId.orEmpty() !in selectedLinkIds }
            .mapNotNull { it.id }
            .forEach { enrollmentId -> KtorApiClient.deleteStudentSubjectEnrollment(enrollmentId).getOrElse { throw it } }
    }

    fun invalidateCaches() {
        cachedStudentsPage = null
        cachedClassSectionLabels = null
    }

    private fun buildClassSectionLabel(classSection: ClassSectionResponse): String {
        val className = classSection.className.orEmpty().trim().ifBlank { "Class" }
        val sectionName = classSection.sectionName.orEmpty().trim()
        return if (sectionName.isBlank()) className else "$className-$sectionName"
    }
}
